import type { Edge } from '@xyflow/react';
import ELK from 'elkjs/lib/elk.bundled.js';

import type { AutomationNode } from '../nodes/types';

const elk = new ELK();

export type LayoutDirection = 'DOWN' | 'RIGHT';

export type LayoutConfig = {
  direction: LayoutDirection;
  nodeSpacing: number;
  layerSpacing: number;
};

const DEFAULT_CONFIG: LayoutConfig = {
  direction: 'DOWN',
  nodeSpacing: 50,
  layerSpacing: 100,
};

export async function getLayoutedElements(
  nodes: AutomationNode[],
  edges: Edge[],
  config: Partial<LayoutConfig> = {},
): Promise<{ nodes: AutomationNode[]; edges: Edge[] }> {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  const elkOptions = {
    'elk.algorithm': 'layered',
    'elk.direction': mergedConfig.direction,
    'elk.spacing.nodeNode': String(mergedConfig.nodeSpacing),
    'elk.layered.spacing.nodeNodeBetweenLayers': String(
      mergedConfig.layerSpacing,
    ),
    'elk.edgeRouting': 'ORTHOGONAL',
  };

  // Build ELK graph from nodes and edges
  const graph = {
    id: 'root',
    layoutOptions: elkOptions,
    children: nodes.map((node) => ({
      id: node.id,
      // Use measured dimensions if available, otherwise use defaults
      width: node.measured?.width ?? 280,
      height: node.measured?.height ?? 100,
    })),
    edges: edges.map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  try {
    // Call elk.layout() to compute positions
    const layoutedGraph = await elk.layout(graph);

    // Map positions back to nodes
    const layoutedNodes = nodes.map((node) => {
      const layoutedNode = layoutedGraph.children?.find(
        (child) => child.id === node.id,
      );

      if (layoutedNode) {
        return {
          ...node,
          position: {
            x: layoutedNode.x ?? node.position.x,
            y: layoutedNode.y ?? node.position.y,
          },
        };
      }

      return node;
    });

    // Return updated nodes with edges unchanged
    return {
      nodes: layoutedNodes,
      edges,
    };
  } catch (error) {
    console.error('Error during ELK layout:', error);
    // Return original nodes/edges if layout fails
    return { nodes, edges };
  }
}
